import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Flex, HStack, Text, Icon, Button, Skeleton, VStack } from "@chakra-ui/react";
import { HiOutlineClipboardDocumentList, HiOutlinePlus, HiOutlineArrowTopRightOnSquare } from "react-icons/hi2";
import { useNavigate } from "react-router-dom";
import { BASE_API_URL } from "../../Components/config";
import { c } from "../classroomColors";
import { formatDay } from "./sessionTokens";

const API = `${BASE_API_URL}/api/v1`;

/** One assignment set after this class, with how far the class has got. */
const AssignmentRow = ({ item, isInstructor, onOpen }) => {
  const pct = item.total ? Math.round((item.submitted / item.total) * 100) : 0;
  return (
    <Flex
      align="center"
      gap="14px"
      px="12px"
      py="11px"
      borderRadius="10px"
      transition="background 0.15s"
      _hover={{ bg: c.hover }}
    >
      <Flex
        boxSize="32px"
        borderRadius="8px"
        align="center"
        justify="center"
        flexShrink={0}
        bg={c.surfaceSubtle}
        color={c.textSub}
      >
        <Icon as={HiOutlineClipboardDocumentList} boxSize="16px" />
      </Flex>

      <Box flex="1 1 auto" minW={0}>
        <Text fontSize="14px" fontWeight="500" color={c.text} isTruncated>
          {item.name}
        </Text>
        <Text fontSize="12px" fontWeight="300" color={c.textMuted} isTruncated>
          {item.dueDate ? `Due ${formatDay(item.dueDate)}` : "No due date"}
        </Text>
      </Box>

      {isInstructor && (
        <HStack spacing="10px" flexShrink={0} display={{ base: "none", sm: "flex" }}>
          <Box w="80px" h="4px" borderRadius="full" bg={c.track} overflow="hidden">
            <Box h="100%" w={`${pct}%`} bg={c.solidBg} borderRadius="full" transition="width 0.3s" />
          </Box>
          <Text fontSize="12px" color={c.textSub} whiteSpace="nowrap" sx={{ fontVariantNumeric: "tabular-nums" }}>
            {item.submitted} / {item.total} in
          </Text>
        </HStack>
      )}

      <Button
        size="xs"
        h="30px"
        px="11px"
        borderRadius="8px"
        variant="outline"
        borderColor={c.border}
        color={c.textSub}
        fontSize="12px"
        fontWeight="400"
        rightIcon={<Icon as={HiOutlineArrowTopRightOnSquare} boxSize="13px" />}
        _hover={{ bg: c.hover, color: c.text, borderColor: c.textMuted }}
        onClick={() => onOpen(item)}
        flexShrink={0}
      >
        Open
      </Button>
    </Flex>
  );
};

/**
 * Post-class: the work set after this class. Assignments are already linked
 * to a segment, so a session's assignments are the links that point at it.
 */
const SessionAssignments = ({ session, courseGuid, subjectGuid, classroomName, isInstructor }) => {
  const navigate = useNavigate();
  const [links, setLinks] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [error, setError] = useState(null);

  const token = `Bearer ${localStorage.getItem("accessToken")}`;

  const load = useCallback(async () => {
    if (!session?.guid) return;
    try {
      const [linkRes, attemptRes] = await Promise.all([
        fetch(`${API}/course-segment-paper-links/by-course-segment-guid/${session.guid}`, {
          headers: { Authorization: token },
        }),
        fetch(
          `${API}/user-course-links/user-paper-attempts/detail/?course_user_type=${
            isInstructor ? "INSTRUCTOR" : "STUDENT"
          }&course_guid=${courseGuid}&limit=100`,
          { headers: { Authorization: token } }
        ),
      ]);
      setLinks(linkRes.ok ? await linkRes.json() : []);
      setAttempts(attemptRes.ok ? await attemptRes.json() : []);
      setError(linkRes.ok ? null : "Could not load the work set after this class.");
    } catch (e) {
      console.error(e);
      setLinks([]);
      setError("Could not load the work set after this class.");
    }
  }, [session?.guid, courseGuid, isInstructor, token]);

  useEffect(() => {
    load();
  }, [load]);

  // The links say what was set; the attempts say how the class is doing on it.
  const items = useMemo(() => {
    if (!links) return null;
    return links.map((link) => {
      const paperGuid = link.custom_paper_guid || link.past_paper_guid;
      const rows = attempts.filter(
        (a) => (a.custom_paper_guid || a.past_paper_guid) === paperGuid
      );
      return {
        guid: link.guid,
        paperGuid,
        name: rows[0]?.paper_name || link.paper_name || "Assignment",
        dueDate: link.due_date,
        total: rows.length,
        submitted: rows.filter((a) => a.submission_status === "SUBMITTED").length,
        attempt: rows[0],
      };
    });
  }, [links, attempts]);

  const openAssignment = (item) => {
    // The teacher reviews a submission from the dashboard; from here the
    // useful jump is the student's own attempt when there is one.
    if (item.attempt?.guid) {
      navigate(`/classroom/report/${item.attempt.guid}`, {
        state: { viewerRole: isInstructor ? "INSTRUCTOR" : "STUDENT" },
      });
    }
  };

  const createForSession = () => {
    navigate("/classroom/teacher/create-paper", {
      state: {
        subjectGuid,
        courseGuid,
        classroomName,
        userType: "INSTRUCTOR",
        paperType: "ASSIGNMENT",
        courseSegmentGuid: session.guid,
        sessionName: session.name,
      },
    });
  };

  if (items === null) {
    return (
      <VStack align="stretch" spacing="8px" pt="4px">
        {[0, 1].map((i) => (
          <Skeleton key={i} h="54px" borderRadius="10px" startColor={c.surfaceSubtle} endColor={c.hover} />
        ))}
      </VStack>
    );
  }

  return (
    <Box>
      {isInstructor && (
        <Flex justify="flex-end" mb={items.length ? "6px" : "0"}>
          <Button
            leftIcon={<Icon as={HiOutlinePlus} boxSize="14px" />}
            size="sm"
            h="32px"
            px="12px"
            borderRadius="8px"
            bg={c.solidBg}
            color={c.solidText}
            fontSize="12.5px"
            fontWeight="500"
            _hover={{ opacity: 0.88 }}
            onClick={createForSession}
          >
            Set work
          </Button>
        </Flex>
      )}

      {error ? (
        <Text fontSize="13px" color={c.textSub} textAlign="center" py="28px">
          {error}
        </Text>
      ) : items.length === 0 ? (
        <Flex direction="column" align="center" justify="center" py="44px" gap="8px" textAlign="center">
          <Flex boxSize="42px" borderRadius="full" bg={c.surfaceSubtle} align="center" justify="center">
            <Icon as={HiOutlineClipboardDocumentList} boxSize="18px" color={c.textMuted} />
          </Flex>
          <Text fontSize="15px" fontWeight="500" color={c.text}>
            No work set after this class
          </Text>
          <Text fontSize="13px" fontWeight="300" color={c.textSub} maxW="340px">
            {isInstructor
              ? "Set an assignment and it will sit with this session."
              : "Your teacher hasn't set anything after this class yet."}
          </Text>
        </Flex>
      ) : (
        <VStack align="stretch" spacing="2px">
          {items.map((item) => (
            <AssignmentRow key={item.guid} item={item} isInstructor={isInstructor} onOpen={openAssignment} />
          ))}
        </VStack>
      )}
    </Box>
  );
};

export default SessionAssignments;
